import { PaymentMethod } from "#common/enums";
import { CountryObjectType, GetCountryCodesQuery } from "#graphql/supergraphTypes";
import { PayeeBaseAddress } from "../../../PayeeWorkflows/types";
import {
  DetailedAchPaymentPrefObjectType,
  DetailedWirePaymentPrefObjectType,
} from "../../common/types";

/**
 * Helper function to get country data from country ID
 */
const getCountryFromCountryData = (
  countryId?: string,
  countryData?: GetCountryCodesQuery,
): Pick<CountryObjectType, "id" | "name"> | null => {
  if (!countryData?.countries?.edges || !countryId) return null;

  const country = countryData.countries.edges.find(
    (edge) => edge?.node?.alpha2code === countryId,
  );
  return country?.node ?? null;
};

/**
 * Creates an initial wire payment preference object from payee data
 * to pre-populate account holder name and address fields
 */
export const createInitialWirePaymentPrefFromPayee = (
  payeeName?: string,
  payeeAddress?: PayeeBaseAddress,
  countryData?: GetCountryCodesQuery,
): DetailedWirePaymentPrefObjectType | undefined => {
  // Only create if we have at least payeeName or payeeAddress
  if (!payeeName && !payeeAddress) {
    return undefined;
  }

  const country = getCountryFromCountryData(payeeAddress?.countryId || "", countryData);
  const isCountryUSA = country?.name === "United States";

  const address = payeeAddress
    ? {
        street: payeeAddress.street || null,
        street2: payeeAddress.street2 || null,
        street3: null,
        city: payeeAddress.city || null,
        postalCode: payeeAddress.postalCode || null,
        // For non-USA countries, use region field for state/province
        region: isCountryUSA ? null : payeeAddress.region || null,
        // For USA countries, use state field
        state: isCountryUSA
          ? {
              __typename: "StateObjectType" as const,
              code: payeeAddress.stateId ?? "",
              id: "",
              name: "",
            }
          : null,
        country: country
          ? {
              id: country.id,
              name: country.name,
              alpha2code: null,
              code: null,
            }
          : null,
      }
    : null;

  const result = {
    __typename: "WirePaymentPrefObjectType" as const,
    id: null,
    paymentPrefId: null,
    paymentMethod: PaymentMethod.Wire,
    beneficiaryName: payeeName || null,
    beneficiaryAddress: address,
    beneficiaryAccountNumber: null,
    beneficiaryType: null,
    bank: null,
    intermediaryBank: null,
    bankInfoRedacted: null,
    naturalPersonDateOfBirth: null,
    naturalPersonEmailAddress: null,
    naturalPersonFirstName: null,
    naturalPersonLastName: null,
    naturalPersonMiddleName: null,
    referenceToBeneficiary: null,
    wireObi: null,
    wireBbi: null,
  };

  return result;
};

/**
 * Creates an initial ACH payment preference object from payee data
 * to pre-populate account holder name field
 */
export const createInitialAchPaymentPrefFromPayee = (
  payeeName?: string,
): DetailedAchPaymentPrefObjectType => {
  const result = {
    __typename: "AchPaymentPrefObjectType" as const,
    id: null,
    paymentPrefId: null,
    paymentMethod: PaymentMethod.ACH,
    accountName: payeeName || null,
    accountNumber: null,
    accountType: null,
    accountOwnership: null,
    accountHolderType: null,
    additionalInstructions: null,
    bankInfoRedacted: null,
    bankName: null,
    naturalPersonDateOfBirth: null,
    naturalPersonEmailAddress: null,
    naturalPersonFirstName: null,
    naturalPersonLastName: null,
    naturalPersonMiddleName: null,
    routingNumber: null,
    signatureFullName: null,
  };

  return result;
};
